import React from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ConfirmOptions } from '~/lib/AppContext';
import ConfirmModal from './ConfirmModal';

type PendingConfirm = ConfirmOptions & { resolve: (value: boolean) => void; key: number };

class ConfirmState {
    @observable
    pending?: PendingConfirm = undefined;

    constructor() {
        makeObservable(this);
    }
}

const confirmState = new ConfirmState();

// Monotonic per-prompt id used as the ConfirmModal React key, so each confirm()
// gets a fresh modal instance (busy/error/acknowledged all reset) even if a new
// prompt opens before the previous one's unmount render has cleared `pending`.
let confirmSeq = 0;

export function confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise<boolean>(
        action((resolve: (value: boolean) => void) => {
            confirmState.pending = { ...options, resolve, key: ++confirmSeq };
        })
    );
}

function handleResult(result: boolean, key: number) {
    runInAction(() => {
        const pending = confirmState.pending;
        // Ignore a late result from a prompt that a newer confirm() has replaced —
        // e.g. prompt A's awaited perform resolving after prompt B opened would
        // otherwise resolve B's promise and fire B's onSuccess.
        if (!pending || pending.key !== key) return;
        // Unmount, resolve, then fire onSuccess. It only fires on the async path
        // (asyncAction present) — a `true` result on the legacy path just means
        // "user confirmed", not "action succeeded".
        confirmState.pending = undefined;
        pending.resolve(result);
        if (result && pending.asyncAction?.onSuccess) {
            try {
                pending.asyncAction.onSuccess();
            } catch (e) {
                // A faulty onSuccess (e.g. a toast that throws) must not corrupt the
                // confirm lifecycle or surface as an unhandled rejection.
                console.error('ConfirmModal onSuccess handler threw', e);
            }
        }
    });
}

@observer
export default class ConfirmModalHost extends React.Component {
    render() {
        if (!confirmState.pending) return null;
        const { title, message, warning, confirmText, cancelText, confirmColor, acknowledgement, asyncAction, key } =
            confirmState.pending;
        return (
            <ConfirmModal
                key={key}
                title={title}
                message={message}
                warning={warning}
                confirmText={confirmText}
                cancelText={cancelText}
                confirmColor={confirmColor}
                acknowledgement={acknowledgement}
                perform={asyncAction?.perform}
                renderError={asyncAction?.renderError}
                onConfirmed={() => handleResult(true, key)}
                onCancel={() => handleResult(false, key)}
            />
        );
    }
}
